// Standalone creative wizard backed by the canonical creative/upload APIs.

import { BuilderSummary } from '@/components/builder-summary';
import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Icons, ChannelPill } from '@/components/primitives';
import { SearchableDropdown } from '@/components/dropdowns';
import {
  useApproveCreative,
  useCreateCreative,
  usePatchCreative,
  useSubmitCreativeForReview,
} from '@/api/manage-hooks';
import { useProductions, useVenues } from '@/api/hooks';
import type { Creative } from '@/api/types';
import { ApiError } from '@/api/errors';
import {
  adaptProductionForLegacyManage,
  VENUE_UNKNOWN,
  type LegacyManageProduction,
} from '@/lib/manage-screen-adapters';
import { uploadCreativePlacement } from '@/api/creative-upload';
import { creativePatch } from '@/lib/creative-patch';
import { holdRelease } from '@/lib/release-hold';
import type {
  CreativeSlot,
  CreativeSlotAsset,
  CtvSlotData,
  DspBanner,
  DspSlotData,
  MetaAssetsBySlot,
  MetaCreativeSlot,
} from '@/lib/creative-required-issues';
import { DISPLAY_ANIMATION_MAX_MS, DISPLAY_FORMATS_TEXT, formatDisplayKb } from '@/lib/media-validation/display';
import {
  RequiredField,
  useRequiredFeedback,
  type RequiredIssue,
} from '@/components/required-field';
import { MetaPreviewGallery } from '@/components/meta-preview-gallery';
import { Section } from '@/components/wizard-section';
import { CreativeBuilder } from './creative';
import {
  CHANNEL_FORMATS,
  isValidCreativeDestinationUrl,
  isValidMetaDisplayUrl,
  metaCopyIssues,
  metaDefaults,
  serializeMetaCreativeSpec,
  type CreativeChannel,
} from './creative-config';
import { PageActions } from '@/components/page-actions';
import { formatDecimalSize } from '@/lib/media-validation/sizes';

// New Creative — standalone wizard matching the New Audience / New Campaign pattern.
// Sections: Name → Production → Channel → Assets → Approval → Review & Save.
// Right rail: channel spec reminders (formats, durations, max bytes).

/** A slot asset picked in this browser — the only kind this flow uploads.
 *  Library previews carry no `File`. */
function isLocalUpload<Asset extends { file?: File }>(
  asset: Asset | null | undefined,
): asset is Asset & { file: File } {
  return !!asset?.file;
}

/** Any channel's local upload: a Meta or CTV file, or a Display banner (a
 *  `DspBanner` is a `CreativeSlotAsset` with its size and animation). */
type LocalUpload = CreativeSlotAsset & { file: File };

type WizardSection = 'name' | 'production' | 'channel' | 'assets' | 'approval' | 'review';
type WizardField = 'creative-name' | 'creative-production' | 'creative-channel' | 'creative-assets';
interface WizardIssue extends RequiredIssue {
  key: WizardField;
}

/** One row of the right rail: label, value. */
type SpecRow = readonly [label: string, value: string];
interface SpecReminders {
  sectionTitle: string;
  rows: SpecRow[];
  limits: SpecRow[];
}

export interface LocalCreativeApprovalProps {
  channel: CreativeChannel;
  metaAssets: MetaAssetsBySlot;
  ctvData: Record<string, CtvSlotData>;
  dspData: Record<string, DspSlotData>;
  confirmed: boolean;
  disabled: boolean;
  onConfirm: () => void;
  onEdit: () => void;
}

function LocalCreativeApproval({
  channel,
  metaAssets,
  ctvData,
  dspData,
  confirmed,
  disabled,
  onConfirm,
  onEdit,
}: LocalCreativeApprovalProps) {
  const items = ((): Array<{ key: string; label: string; asset: LocalUpload }> => {
    if (channel === 'meta') {
      return Object.entries(metaAssets['nc-meta-1'] || {})
        .flatMap(([key, asset]) => (isLocalUpload(asset) ? [{ key, label: `Meta · ${key}`, asset }] : []));
    }
    if (channel === 'ctv') {
      const asset = ctvData['nc-ctv-1']?.asset;
      return isLocalUpload(asset) ? [{ key: 'ctv_16x9', label: 'CTV · 16:9', asset }] : [];
    }
    return (dspData['nc-dsp-1']?.banners || [])
      .filter(isLocalUpload<DspBanner>)
      .map((asset, index) => ({
        key: `${asset.id}-${index}`,
        label: `Display · ${asset.w}×${asset.h}`,
        asset,
      }));
  })();

  return (
    <div>
      <div className="muted mb-3" style={{fontSize:11.5,lineHeight:1.5}}>
        Review the browser-local files below. Saving uploads them directly to file storage;
        this approval records a local library decision. This screen makes no direct
        Meta or TTD calls.
      </div>
      <div className="grid-3" style={{gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:12}}>
        {items.map(({ key, label, asset }) => (
          <div key={key} className="panel" style={{padding:8}}>
            <div className="creative-asset" style={{aspectRatio:'16/10',position:'relative',overflow:'hidden'}}>
              {asset.type === 'video'
                ? <video src={asset.url} muted controls playsInline style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                : <img src={asset.url} alt={asset.name} style={{width:'100%',height:'100%',objectFit:'cover'}}/>}
            </div>
            <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:11.5,marginTop:7}}>{label}</div>
            <div className="muted mono" style={{fontSize:9.5,marginTop:3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{asset.name}</div>
            <div className="muted mono" style={{fontSize:9.5,marginTop:2}}>
              {asset.width ?? asset.w}×{asset.height ?? asset.h}
              {asset.durationMs ? ` · ${(asset.durationMs / 1000).toFixed(1)}s` : ''}
            </div>
          </div>
        ))}
      </div>
      <div className="flex between items-center mt-3" style={{gap:10}}>
        <div className="muted" style={{fontSize:11.5}}>
          {confirmed
            ? <><span style={{color:'var(--good)'}}>{Icons.check}</span> Local approval confirmed.</>
            : 'Leave unconfirmed to submit the uploaded creative for review.'}
        </div>
        {confirmed
          ? <button className="btn btn-sm" disabled={disabled} onClick={onEdit}>Undo approval</button>
          : <button className="btn btn-primary" disabled={disabled} onClick={onConfirm}>{Icons.check} Approve locally</button>}
      </div>
    </div>
  );
}

export interface NewCreativeFlowProps {
  /** Leaves the flow. The /creatives/new route navigates to the library; the
   *  campaign workspace closes its overlay instead (and may keep it open to ask
   *  about unsaved work). Called with the library's view id. */
  setView: (view: 'creatives') => void;
  /** Pins the production (the campaign workspace passes its campaign's). */
  initialProductionId?: string;
  /** Pins the channel; set, the flow renders as a dialog section, not a page. */
  initialChannel?: CreativeChannel | '';
  heading?: string;
  subtitle?: string;
  /** Replaces the navigation after a save with the new creative's id. */
  onSaved?: ((id: string) => void) | null;
  onDirtyChange?: ((dirty: boolean) => void) | null;
}

function NewCreativeFlow({ setView, initialProductionId = '', initialChannel = '', heading = '', subtitle = '', onSaved = null, onDirtyChange = null }: NewCreativeFlowProps) {
  const { data: apiProductions = [], isLoading: productionsLoading } = useProductions();
  const { data: venueRows = [] } = useVenues();
  const createCreative = useCreateCreative();
  const patchCreative = usePatchCreative();
  const submitForReview = useSubmitCreativeForReview();
  const approveCreative = useApproveCreative();
  const {
    messages: requiredMessages,
    attempt: requiredAttempt,
    report: reportRequired,
    clear: clearRequired,
    clearAll: clearAllRequired,
  } = useRequiredFeedback();
  // `venue` was `p.venue_id || 'Venue unavailable'` — an id, shown to people in
  // three places below. Resolve the name or carry null; the placeholder is
  // applied at render, never stored on the object.
  const venueNames = useMemo(
    () => new Map(venueRows.map((venue): [string, string | null] => [venue.id, venue.name])),
    [venueRows],
  );
  // The campaign wizard's adapter, so the picker reads the same as there: the
  // event dates were shown as raw ISO timestamps and a production with no genre
  // read "null" in its sublabel.
  const PRODUCTIONS = useMemo(() => apiProductions.map((p): LegacyManageProduction => ({
    ...adaptProductionForLegacyManage(p, venueNames),
    posterBg: 'var(--ink-3)',
  })), [apiProductions, venueNames]);

  const productionMap = useMemo(
    () => Object.fromEntries(PRODUCTIONS.map(s => [s.id, s])),
    [PRODUCTIONS]
  );

  const nameInputId = useId();
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [productionId, setProductionId] = useState(initialProductionId);
  const [channel, setChannel] = useState<CreativeChannel | ''>(initialChannel);
  const [approvalConfirmed, setApprovalConfirmed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStage, setSaveStage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [previewCreativeId, setPreviewCreativeId] = useState<string | null>(null);
  // The draft this flow created and the server's latest copy of it; the two
  // are set together, by the create, and refreshed by each later write.
  const draftRef = useRef<{ id: string; snapshot: Creative } | null>(null);
  // Placement tag -> the file uploaded under it. A tag is skipped on a later
  // save only while it still holds that same file: a replaced file uploads again.
  const uploadedFilesRef = useRef(new Map<string, File>());
  const submittedRef = useRef(false);
  const saveAbortRef = useRef<AbortController | null>(null);

  // Per-channel state for the embedded builders (in single-creative mode).
  const [metaCreatives, setMetaCreatives] = useState<MetaCreativeSlot[]>([{ id: 'nc-meta-1', label: 'Creative 1', metaFields: {} }]);
  const [metaAssets, setMetaAssets] = useState<MetaAssetsBySlot>({});
  const [ctvCreatives, setCtvCreatives] = useState<CreativeSlot[]>([{ id: 'nc-ctv-1', label: 'Creative 1' }]);
  const [ctvData, setCtvData] = useState<Record<string, CtvSlotData>>({ 'nc-ctv-1': { destUrl: '', asset: null } });
  const [dspGroups, setDspGroups] = useState<CreativeSlot[]>([{ id: 'nc-dsp-1', label: 'Banner Group 1' }]);
  const [dspData, setDspData] = useState<Record<string, DspSlotData>>({ 'nc-dsp-1': { destUrl: '', banners: [] } });

  // Wizard open/done state per section.
  const [open, setOpen] = useState<Record<WizardSection, boolean>>({ name: true, production: false, channel: false, assets: false, approval: false, review: false });
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const [productionConfirmed, setProductionConfirmed] = useState(false);
  const [channelConfirmed, setChannelConfirmed] = useState(false);
  const [assetsConfirmed, setAssetsConfirmed] = useState(false);

  const production = productionMap[productionId];
  const channelCfg = channel ? CHANNEL_FORMATS[channel] : null;
  const channelLabel = channel === 'meta' ? 'Meta' : channel === 'ctv' ? 'CTV' : channel === 'dsp' ? 'Display' : '';
  // A campaign pins the channel, so this flow is being hosted inside the
  // campaign workspace's overlay rather than shown as the /creatives/new page.
  const embedded = !!initialChannel;
  const headingText = heading
    || (embedded ? `New ${channelLabel || 'campaign'} creative` : 'New Creative');
  const subtitleText = subtitle
    || (embedded
      ? 'Built for this campaign · saved to the library, applied only when you apply it'
      : 'Build a reusable creative for the library · use it later in any campaign');
  const metaFormValues = {
    ...metaDefaults(production, 'meta', CHANNEL_FORMATS.meta.name, ''),
    ...(metaCreatives[0]?.metaFields || {}),
  };
  const destinationUrl = (channel === 'meta'
    ? metaFormValues.dest_url
    : channel === 'ctv'
      ? ctvData['nc-ctv-1']?.destUrl
      : channel === 'dsp'
        ? dspData['nc-dsp-1']?.destUrl
        : '') ?? '';
  useEffect(() => {
    onDirtyChange?.(nameTouched || !!destinationUrl || Object.keys(metaAssets).length > 0 ||
      Object.values(ctvData).some((data) => !!data.asset) || Object.values(dspData).some((data) => data.banners?.some((asset) => isLocalUpload(asset))));
  }, [nameTouched, destinationUrl, metaAssets, ctvData, dspData, onDirtyChange]);
  const toggle = (k: WizardSection) => setOpen(o => ({ ...o, [k]: !o[k] }));

  // Auto-suggest name when production + channel become available, unless the
  // user has already typed something.
  useEffect(() => {
    if (nameTouched) return;
    if (production && channel) {
      setName(`${production.title} · ${channelLabel}`);
      clearRequired('creative-name');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionId, channel]);
  useEffect(() => () => saveAbortRef.current?.abort(), []);

  const cancel = () => {
    saveAbortRef.current?.abort();
    setView('creatives');
  };

  const APPROVAL_STATES_BY_STATUS = {
    draft: { label: 'Save as Draft',  cta: 'Save as Draft',       hint: 'Assets not yet uploaded' },
    review: { label: 'Submit for Review', cta: 'Submit for Review', hint: 'Assets uploaded · awaiting approval' },
    approved: { label: 'Save · Approved', cta: 'Save · Approved',  hint: 'All placements approved' },
  };

  // Quick checks for whether assets exist (mirrors the per-channel "empty"
  // logic used elsewhere).
  const assetsPresent = (() => {
    if (channel === 'meta') {
      const a = metaAssets['nc-meta-1'];
      return !!a && Object.values(a).some(asset => isLocalUpload(asset));
    }
    if (channel === 'ctv') return isLocalUpload(ctvData['nc-ctv-1']?.asset);
    if (channel === 'dsp') return (dspData['nc-dsp-1']?.banners || []).some(asset => isLocalUpload(asset));
    return false;
  })();
  const destinationPresent = isValidCreativeDestinationUrl(destinationUrl);
  const displayLinkPresent = channel !== 'meta'
    || isValidMetaDisplayUrl(metaFormValues.display_url);
  // post_text / headline / cta are required by the API but were gated nowhere
  // on the client, so an emptied field reached the server as a bare 422.
  const copyIssues = channel === 'meta'
    ? metaCopyIssues(metaFormValues).filter((issue) => issue.key !== 'display_url')
    : [];
  const assetsConfigComplete = assetsPresent
    && destinationPresent
    && displayLinkPresent
    && copyIssues.length === 0;
  const assetsConfigMessage = !assetsPresent
    && !destinationPresent
    ? 'Upload at least one asset and enter a valid HTTPS destination URL.'
    : !assetsPresent
      ? 'Upload at least one asset before continuing.'
      : !destinationPresent
        ? 'Enter a valid HTTPS destination URL before continuing.'
        : !displayLinkPresent
          ? 'Enter a valid display link before continuing.'
          : copyIssues.length > 0
            ? copyIssues.map((issue) => issue.message).join(' ')
        : '';

  // Derived approval status — driven by section 5's preview-approval state and
  // whether the user has uploaded any assets. Mirrors the campaign wizard's
  // "Preview & Approve" semantics: 'approved' when all placements are signed
  // off, otherwise 'review' once assets exist, otherwise 'draft'.
  const derivedStatus = approvalConfirmed ? 'approved' : (assetsPresent ? 'review' : 'draft');

  const canSave =
    name.trim().length > 0 &&
    !!productionId &&
    !!channel &&
    assetsConfigComplete;

  const save = async ({ forPreview = false }: { forPreview?: boolean } = {}): Promise<string | undefined> => {
    // `canSave` already requires a channel; the check names it for the compiler.
    if (!canSave || isSaving || !channel) return;
    setIsSaving(true);
    setSaveError('');
    const saveAbort = new AbortController();
    saveAbortRef.current = saveAbort;

    const ctvDuration = ctvData['nc-ctv-1']?.asset?.duration;
    const firstBanner = dspData['nc-dsp-1']?.banners[0];
    const formatSummary =
      channel === 'meta' ? 'Ad Set · 1:1 · 4:5 · 9:16'
      : channel === 'ctv' ? (ctvDuration ? `${ctvDuration}s · 16:9` : '30s · 16:9')
      : (dspData['nc-dsp-1']?.banners || []).map(b => `${b.w}×${b.h}`).join(' + ') || 'IAB set';
    const mediaType = ((): 'image' | 'video' => {
      if (channel === 'meta') {
        const a = metaAssets['nc-meta-1'];
        const first = a ? Object.values(a).find(Boolean) : null;
        return first?.type || 'image';
      }
      if (channel === 'ctv') return 'video';
      return 'image';
    })();
    const spec =
      channel === 'meta' ? serializeMetaCreativeSpec(metaFormValues)
      : channel === 'ctv' ? '1920×1080'
      : (firstBanner ? `${firstBanner.w}×${firstBanner.h}` : 'IAB set');

    const placements = ((): Array<{ placementTag: string; asset: LocalUpload }> => {
      if (channel === 'meta') {
        return Object.entries(metaAssets['nc-meta-1'] || {})
          .flatMap(([key, asset]) => (isLocalUpload(asset) ? [{ placementTag: `meta_${key}`, asset }] : []));
      }
      if (channel === 'ctv') {
        const asset = ctvData['nc-ctv-1']?.asset;
        return isLocalUpload(asset) ? [{ placementTag: 'ctv_16x9', asset }] : [];
      }
      return (dspData['nc-dsp-1']?.banners || [])
        .filter(isLocalUpload<DspBanner>)
        .map((asset, index) => ({
          placementTag: `dsp_${asset.w}x${asset.h}_${index + 1}`,
          asset,
        }));
    })();

    // Create, uploads, submit and approval: a new release waits for the whole save, which
    // carries on if the operator leaves the page (2026-10-21).
    const releaseHold = holdRelease('creative_save');
    try {
      let draft = draftRef.current;
      if (!draft) {
        const newId = `creative_${crypto.randomUUID().replaceAll('-', '').slice(0, 24)}`;
        setSaveStage('Creating draft…');
        const created = await createCreative.mutateAsync({
          id: newId,
          name: name.trim(),
          production_id: productionId,
          channel,
          media_type: mediaType,
          format: formatSummary,
          spec,
          destination_url: destinationUrl.trim(),
          agency: null,
        });
        draft = { id: newId, snapshot: created };
        draftRef.current = draft;
      } else {
        setSaveStage('Updating draft configuration…');
        const { creative: updated } = await patchCreative.mutateAsync({
          id: draft.id,
          patch: creativePatch(draft.snapshot, {
            name: name.trim(),
            format: formatSummary,
            spec,
            destination_url: destinationUrl.trim(),
          }),
        });
        draft.snapshot = updated;
      }
      const creativeId = draft.id;

      for (const [index, placement] of placements.entries()) {
        if (uploadedFilesRef.current.get(placement.placementTag) === placement.asset.file) continue;
        const width = placement.asset.width ?? placement.asset.w;
        const height = placement.asset.height ?? placement.asset.h;
        // The builder measures every file it accepts, so this cannot happen;
        // the uploader needs the size, and a guess would be attached as fact.
        if (width == null || height == null) {
          throw new Error(`${placement.asset.name} has no measured size; pick the file again.`);
        }
        setSaveStage(`Uploading asset ${index + 1} of ${placements.length}…`);
        draft.snapshot = await uploadCreativePlacement({
          creativeId,
          placementTag: placement.placementTag,
          asset: {
            ...placement.asset,
            width,
            height,
            durationMs: placement.asset.durationMs ?? undefined,
          },
          signal: saveAbort.signal,
          onProgress: (uploaded, total) => {
            const percent = Math.round((uploaded / total) * 100);
            setSaveStage(`Uploading asset ${index + 1} of ${placements.length} · ${percent}%`);
          },
        });
        uploadedFilesRef.current.set(placement.placementTag, placement.asset.file);
      }

      const targetStatus = forPreview ? 'review' : derivedStatus;
      if (targetStatus === 'review' || targetStatus === 'approved') {
        if (!submittedRef.current) {
          setSaveStage('Submitting for review…');
          await submitForReview.mutateAsync(creativeId);
          submittedRef.current = true;
        }
      }
      if (forPreview) {
        setPreviewCreativeId(creativeId);
        setSaveStage('Official Meta previews are being generated…');
        return creativeId;
      }
      if (targetStatus === 'approved') {
        setSaveStage('Recording local approval…');
        await approveCreative.mutateAsync(creativeId);
        // Provider publishing runs entirely server-side (self-scheduled queue
        // polling + the minute cron); the library shows its progress.
        setSaveStage('Approved. Publishing continues in the background…');
        if (onSaved) onSaved(creativeId); else setView('creatives');
        return;
      }

      setSaveStage('Saved');
      if (onSaved) onSaved(creativeId); else setView('creatives');
    } catch (error) {
      // A missing deployment setting is not retryable: telling the operator to
      // retry sends them into a loop against a fault only an admin can clear.
      const retryable = !(error instanceof ApiError && error.code === 'not_configured');
      const draftId = draftRef.current?.id;
      const retained = draftId && retryable
        ? ` Draft ${draftId} was retained; retry resumes client-confirmed steps.`
        : draftId
          ? ` Draft ${draftId} was retained.`
          : '';
      const reason = error instanceof Error && error.message ? error.message : 'Could not save the creative.';
      setSaveError(`${reason}${retained}`);
    } finally {
      releaseHold();
      if (saveAbortRef.current === saveAbort) saveAbortRef.current = null;
      setIsSaving(false);
      setSaveStage('');
    }
  };

  const reportField = (issue: WizardIssue) => {
    reportRequired([
      issue,
      ...Object.entries(requiredMessages)
        .filter(([key]) => key !== issue.key)
        .map(([key, message]) => ({ key, message })),
    ]);
  };

  const confirmName = () => {
    if (!name.trim()) {
      setOpen(current => ({ ...current, name: true }));
      reportField({
        key: 'creative-name',
        message: 'Enter a creative name before continuing.',
      });
      return;
    }
    clearRequired('creative-name');
    setNameConfirmed(true);
    setOpen(current => ({ ...current, name: false, production: true }));
  };

  const confirmProduction = () => {
    if (!productionId) {
      setOpen(current => ({ ...current, production: true }));
      reportField({
        key: 'creative-production',
        message: 'Select the production this creative belongs to.',
      });
      return;
    }
    clearRequired('creative-production');
    setProductionConfirmed(true);
    setOpen(current => ({ ...current, production: false, channel: true }));
  };

  const confirmChannel = () => {
    if (!channel) {
      setOpen(current => ({ ...current, channel: true }));
      reportField({
        key: 'creative-channel',
        message: 'Select a delivery channel before continuing.',
      });
      return;
    }
    clearRequired('creative-channel');
    setChannelConfirmed(true);
    setOpen(current => ({ ...current, channel: false, assets: true }));
  };

  const confirmAssets = async () => {
    if (!assetsConfigComplete) {
      setOpen(current => ({ ...current, assets: true }));
      reportField({
        key: 'creative-assets',
        message: assetsConfigMessage,
      });
      return;
    }
    clearRequired('creative-assets');
    if (channel === 'meta') {
      const creativeId = await save({ forPreview: true });
      if (!creativeId) return;
    }
    setAssetsConfirmed(true);
    setOpen(current => ({ ...current, assets: false, approval: true }));
  };

  const attemptSave = () => {
    if (isSaving) return;
    const candidates: Array<WizardIssue | false> = [
      !name.trim() && {
        key: 'creative-name',
        message: 'Enter a creative name before saving.',
      },
      !productionId && {
        key: 'creative-production',
        message: 'Select the production this creative belongs to.',
      },
      !channel && {
        key: 'creative-channel',
        message: 'Select a delivery channel before saving.',
      },
      !assetsConfigComplete && {
        key: 'creative-assets',
        message: assetsConfigMessage,
      },
    ];
    const issues = candidates.filter((issue): issue is WizardIssue => !!issue);

    const [firstIssue] = issues;
    if (firstIssue) {
      const sectionByField: Record<WizardField, WizardSection> = {
        'creative-name': 'name',
        'creative-production': 'production',
        'creative-channel': 'channel',
        'creative-assets': 'assets',
      };
      setOpen(current => ({
        ...current,
        [sectionByField[firstIssue.key]]: true,
      }));
      reportRequired(issues);
      return;
    }

    clearAllRequired();
    void save();
  };

  const specReminders = ((): SpecReminders | null => {
    if (channel === 'meta') {
      const meta = CHANNEL_FORMATS.meta;
      return {
        sectionTitle: 'Meta placements',
        rows: meta.formats.map((f): SpecRow => [f.name, f.spec]),
        limits: [
          ['Image max', formatDecimalSize(meta.limits.image)],
          ['Video max', formatDecimalSize(meta.limits.video)],
          ['Aspect tol.', `±${(meta.limits.ratioTol * 100).toFixed(0)}%`],
        ],
      };
    }
    if (channel === 'ctv') {
      const c = CHANNEL_FORMATS.ctv.ctv;
      return {
        sectionTitle: 'CTV specs',
        rows: [
          ['Aspect',   '16:9'],
          ['Min size', `${c.minW}×${c.minH}`],
          ['Durations', `${c.durations.join('s or ')}s`],
        ],
        limits: [
          ['Max bytes',   `${(c.maxBytes / 1024 / 1024).toFixed(0)} MB`],
          ['Duration tol.', `±${c.durTol}s`],
          ['Codec',       'MP4 · H.264'],
        ],
      };
    }
    if (channel === 'dsp') {
      const d = CHANNEL_FORMATS.dsp.dsp;
      return {
        sectionTitle: 'Display sizes',
        rows: d.sizes.map((s): SpecRow => [s.name, `${s.w}×${s.h}`]),
        limits: [
          ['Max bytes', formatDisplayKb(d.maxBytes)],
          ['Aspect tol.', d.tol ? `±${(d.tol * 100).toFixed(0)}%` : 'Exact only'],
          ['Media',     DISPLAY_FORMATS_TEXT],
          ['Animation', `${DISPLAY_ANIMATION_MAX_MS / 1000} s max, then still`],
        ],
      };
    }
    return null;
  })();

  const channelChoices: CreativeChannel[] = initialChannel ? [initialChannel] : ['meta', 'ctv', 'dsp'];

  const cancelButton = (
    <button className="btn btn-ghost btn-icon-phone" data-phone="inline" aria-label="Cancel" onClick={cancel}>{Icons.x} <span className="phone-long">Cancel</span></button>
  );
  const saveButton = (
    <button
      className="btn btn-primary"
      data-phone="inline"
      onClick={attemptSave}
      disabled={isSaving}
      title={!canSave ? 'Complete the highlighted required fields to save' : ''}
    >
      {Icons.check} {isSaving ? (saveStage || 'Saving…') : <><span className="phone-long">{APPROVAL_STATES_BY_STATUS[derivedStatus].cta}</span><span className="phone-short">{APPROVAL_STATES_BY_STATUS[derivedStatus].cta.split(' ')[0]}</span></>}
    </button>
  );

  return (
    <div>
      {/* Inside the campaign workspace's overlay this flow is a section of a
          dialog, not a page: a second <h1>, the library's subtitle and the
          phone page-header machinery all belong to the page behind it. The
          dialog's own heading is an <h2> naming the campaign's channel, which
          is also what ResponsiveOverlay focuses on open. */}
      {embedded ? (
        <div className="drawer-hd">
          <div>
            <h2>{headingText}</h2>
            <div className="muted" style={{marginTop:6,fontSize:11.5}}>{subtitleText}</div>
          </div>
          <div className="flex" style={{gap:8,flexWrap:'wrap'}}>
            {cancelButton}
            {saveButton}
          </div>
        </div>
      ) : (
        <div className="page-hd">
          <div>
            <h1 className="page-title">{headingText}</h1>
            <div className="page-sub" style={{marginTop:6}}>{subtitleText}</div>
          </div>
          <PageActions>
            {cancelButton}
            {saveButton}
          </PageActions>
        </div>
      )}

      <div className="create-wrap">
        <div className="create-main">
          {saveError && (
            <div className="asset-err" style={{margin:'0 0 14px'}}>
              <span style={{flexShrink:0}}>⚠</span>
              <span style={{flex:1}}>{saveError}</span>
              <button className="asset-err-x" onClick={()=>setSaveError('')} title="Dismiss">×</button>
            </div>
          )}

          {/* Section 1 — Name */}
          <Section
            n="1" title="Name"
            summary={nameConfirmed && !open.name ? (name || 'Untitled creative') : 'Give this creative a recognizable internal name'}
            open={open.name} done={nameConfirmed && !!name.trim()}
            onToggle={()=>toggle('name')}
          >
            <RequiredField
              fieldKey="creative-name"
              message={requiredMessages['creative-name']}
              attempt={requiredAttempt}
              className="field"
            >
              {/* One span for the name and its asterisk: `.field-lbl` spreads its children apart,
                  which floated the asterisk to the middle of the row. */}
              <label className="field-lbl" htmlFor={nameInputId}><span>Creative name <span style={{color:'var(--live)'}}>*</span></span>
                <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:8,fontSize:10.5}}>{nameTouched ? 'edited' : 'auto-fills once Production + Channel are picked'}</span>
              </label>
              <input
                id={nameInputId}
                className="input"
                data-validation-focus
                placeholder="e.g. Midnight · Saxophone Cut · 15s"
                value={name}
                onChange={e=>{
                  setName(e.target.value);
                  setNameTouched(true);
                  clearRequired('creative-name');
                }}
              />
            </RequiredField>
            <div className="flex between items-center mt-2" style={{gap:10}}>
              <div className="muted" style={{fontSize:11.5}}>{!name.trim() ? 'Enter a name to continue.' : <><span style={{color:'var(--good)'}}>{Icons.check}</span> Ready.</>}</div>
              <button className="btn btn-primary" disabled={isSaving} onClick={confirmName}>
                {Icons.check} Confirm name
              </button>
            </div>
          </Section>

          {/* Section 2 — Production */}
          <Section
            n="2" title="Production"
            summary={productionConfirmed && !open.production ? (production ? `${production.title} · ${production.venue ?? VENUE_UNKNOWN}` : '—') : 'Which production is this creative for?'}
            open={open.production} done={productionConfirmed && !!productionId}
            onToggle={()=>toggle('production')}
          >
            <RequiredField
              fieldKey="creative-production"
              message={requiredMessages['creative-production']}
              attempt={requiredAttempt}
              className="field"
            >
              <label className="field-lbl">Production <span style={{color:'var(--live)'}}>*</span></label>
              <SearchableDropdown
                value={productionId}
                disabled={!!initialProductionId}
                ariaLabel={initialProductionId ? 'Production from campaign' : 'Production'}
                onChange={(nextProductionId) => {
                  if (!initialProductionId) setProductionId(nextProductionId);
                  clearRequired('creative-production');
                  clearRequired('creative-assets');
                }}
                options={PRODUCTIONS.map(s => ({
                  id: s.id,
                  label: s.title,
                  sublabel: `${s.venue ?? VENUE_UNKNOWN} · ${s.genre} · ${s.dates}`,
                  swatch: s.posterBg,
                }))}
                placeholder={productionsLoading ? 'Loading productions…' : '— Select a production —'}
                searchPlaceholder={`Search ${PRODUCTIONS.length} productions…`}
              />
            </RequiredField>
            <div className="flex between items-center mt-2" style={{gap:10}}>
              <div className="muted" style={{fontSize:11.5}}>{!productionId ? 'Pick a production to continue.' : <><span style={{color:'var(--good)'}}>{Icons.check}</span> {production?.title}.</>}</div>
              <button className="btn btn-primary" disabled={isSaving} onClick={confirmProduction}>
                {Icons.check} Confirm production
              </button>
            </div>
          </Section>

          {/* Section 3 — Channel */}
          <Section
            n="3" title="Channel"
            summary={channelConfirmed && !open.channel && channel ? `${channelLabel} · ${CHANNEL_FORMATS[channel].blurb}` : 'Pick the channel this creative will run on'}
            open={open.channel} done={channelConfirmed && !!channel}
            onToggle={()=>toggle('channel')}
          >
            <RequiredField
              fieldKey="creative-channel"
              message={requiredMessages['creative-channel']}
              attempt={requiredAttempt}
              surface
            >
              <div className="channel-tiles" data-validation-focus tabIndex={-1}>
                {channelChoices.map(ch => {
                  const cfg = CHANNEL_FORMATS[ch];
                  const formats =
                    ch === 'meta' ? cfg.formats.map(f => f.name)
                    : ch === 'ctv' ? ['15s · 16:9', '30s · 16:9']
                    : CHANNEL_FORMATS.dsp.dsp.sizes.slice(0, 4).map(s => `${s.w}×${s.h}`);
                  return (
                    <div role="button" tabIndex={0} aria-pressed={channel === ch} onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();e.currentTarget.click();}}}
                      key={ch}
                      className={`ch-tile ${channel === ch ? 'selected' : ''}`}
                      onClick={()=>{
                        if (initialChannel && initialChannel !== ch) return;
                        setChannel(ch);
                        clearRequired('creative-channel');
                        clearRequired('creative-assets');
                      }}
                    >
                      <div className="ch-tile-hd"><div className="ch-tile-name">{cfg.name}</div><ChannelPill channel={ch}/></div>
                      <div className="muted" style={{fontSize:11.5,lineHeight:1.4}}>{cfg.blurb}</div>
                      <div className="ch-tile-formats">{formats.map(s => <span key={s}>{s}</span>)}</div>
                    </div>
                  );
                })}
              </div>
            </RequiredField>
            <div className="flex between items-center mt-2" style={{gap:10}}>
              <div className="muted" style={{fontSize:11.5}}>{!channel ? 'Pick a channel to continue.' : <><span style={{color:'var(--good)'}}>{Icons.check}</span> {channelLabel}.</>}</div>
              <button className="btn btn-primary" disabled={isSaving} onClick={confirmChannel}>
                {Icons.check} Confirm channel
              </button>
            </div>
          </Section>

          {/* Section 4 — Assets */}
          <Section
            n="4" title="Assets & Configuration"
            summary={assetsConfirmed && !open.assets
              ? `${channelLabel} · ${assetsConfigComplete ? 'assets and destination ready' : 'configuration incomplete'}`
              : (channel ? `Upload and configure ${channelLabel} assets` : 'Pick a channel above to upload assets')}
            open={open.assets} done={assetsConfirmed && assetsConfigComplete}
            onToggle={()=>toggle('assets')}
          >
            <RequiredField
              fieldKey="creative-assets"
              message={requiredMessages['creative-assets']}
              attempt={requiredAttempt}
              surface
            >
              <div data-validation-focus tabIndex={-1}>
                {channel && production ? (
                  <CreativeBuilder
                    channel={channel}
                    production={production}
                    singleMode
                    creatives={metaCreatives}
                    setCreatives={(next) => {
                      clearRequired('creative-assets');
                      setMetaCreatives(next);
                    }}
                    metaAssets={metaAssets}
                    setMetaAssets={(next) => {
                      clearRequired('creative-assets');
                      setMetaAssets(next);
                    }}
                    ctvCreatives={ctvCreatives}
                    setCtvCreatives={(next) => {
                      clearRequired('creative-assets');
                      setCtvCreatives(next);
                    }}
                    ctvData={ctvData}
                    setCtvData={(next) => {
                      clearRequired('creative-assets');
                      setCtvData(next);
                    }}
                    dspGroups={dspGroups}
                    setDspGroups={(next) => {
                      clearRequired('creative-assets');
                      setDspGroups(next);
                    }}
                    dspData={dspData}
                    setDspData={(next) => {
                      clearRequired('creative-assets');
                      setDspData(next);
                    }}
                  />
                ) : (
                  <div className="muted" style={{padding:20,textAlign:'center'}}>Complete sections 2 and 3 first.</div>
                )}
              </div>
            </RequiredField>
            <div className="flex between items-center mt-3" style={{gap:10}}>
              <div className="muted" style={{fontSize:11.5}}>{!assetsConfigComplete ? assetsConfigMessage : <><span style={{color:'var(--good)'}}>{Icons.check}</span> Assets and destination ready.</>}</div>
              {/* Uploading to R2 and saving can take a while on large video.
                  Without a visible working state the button just looks dead and
                  people click it again. */}
              <button
                className={`btn btn-primary${isSaving ? ' btn-busy' : ''}`}
                disabled={isSaving}
                onClick={confirmAssets}
              >
                {isSaving
                  ? <><span className="btn-spinner" aria-hidden="true" />{saveStage || 'Uploading assets…'}</>
                  : <>{Icons.check} Confirm assets</>}
              </button>
            </div>
          </Section>

          {/* Section 5 — Preview & Approve (mirrors the campaign wizard) */}
          <Section
            n="5" title="Preview & Approve"
            summary={
              approvalConfirmed && !open.approval
                ? 'All official placements reviewed · creative can be approved'
                : (assetsPresent
                    ? (channel === 'meta'
                        ? 'Review every official Meta placement'
                        : 'Review the selected files and approve locally (or save as In review)')
                    : 'Upload assets in section 4 to review them')
            }
            open={open.approval} done={approvalConfirmed}
            onToggle={()=>toggle('approval')}
          >
            {assetsPresent && channel && production ? (
              channel === 'meta' && previewCreativeId ? (
                <MetaPreviewGallery
                  creativeId={previewCreativeId}
                  autoRefresh
                  onReviewChange={setApprovalConfirmed}
                />
              ) : <LocalCreativeApproval
                channel={channel}
                metaAssets={metaAssets}
                ctvData={ctvData}
                dspData={dspData}
                confirmed={approvalConfirmed}
                disabled={isSaving}
                onConfirm={()=>{ setApprovalConfirmed(true); setOpen({...open, approval:false, review:true}); }}
                onEdit={()=>setApprovalConfirmed(false)}
              />
            ) : (
              <div className="muted" style={{padding:20,textAlign:'center'}}>Upload assets in section 4 to review them.</div>
            )}
          </Section>

          {/* Section 6 — Review & Save */}
          <Section
            n="6" title="Review & Save"
            summary="Final check before saving to the library"
            open={open.review} done={false}
            onToggle={()=>toggle('review')}
          >
            <div className="grid-2 mb-3" style={{gap:14}}>
              <div className="facet" style={{margin:0}}>
                <div className="facet-hd"><h5>Name</h5></div>
                <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:15,lineHeight:1.25}}>{name || <span className="muted">(missing)</span>}</div>
              </div>
              <div className="facet" style={{margin:0}}>
                <div className="facet-hd"><h5>Production</h5></div>
                <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:15}}>{production?.title || <span className="muted">(missing)</span>}</div>
                {production && <div className="muted mt-1" style={{fontSize:11.5}}>{production.venue ?? VENUE_UNKNOWN} · {production.genre}</div>}
              </div>
              <div className="facet" style={{margin:0}}>
                <div className="facet-hd"><h5>Channel</h5></div>
                <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:15}}>{channelLabel || <span className="muted">(missing)</span>}</div>
                {channelCfg && <div className="muted mt-1" style={{fontSize:11.5}}>{channelCfg.blurb}</div>}
              </div>
              <div className="facet" style={{margin:0}}>
                <div className="facet-hd"><h5>Status</h5></div>
                <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:15}}>
                  {APPROVAL_STATES_BY_STATUS[derivedStatus].label}
                </div>
                <div className="muted mt-1" style={{fontSize:11.5}}>{APPROVAL_STATES_BY_STATUS[derivedStatus].hint}</div>
              </div>
            </div>

            <div className="flex between items-center mt-2" style={{gap:10}}>
              <div className="muted" style={{fontSize:11.5}}>{canSave ? <><span style={{color:'var(--good)'}}>{Icons.check}</span> Ready to save.</> : 'Complete every section above to save.'}</div>
              <div className="flex gap-2">
                <button className="btn" onClick={cancel}>Cancel</button>
                <button className="btn btn-primary" disabled={isSaving} onClick={attemptSave}>
                  {Icons.check} {isSaving ? (saveStage || 'Saving…') : APPROVAL_STATES_BY_STATUS[derivedStatus].cta}
                </button>
              </div>
            </div>
          </Section>
        </div>

        {/* Right rail — channel spec reminders */}
        <BuilderSummary className="create-side">
          <div style={{padding:'16px 14px', display:'flex', flexDirection:'column', gap:14}}>
            {!channel || !specReminders ? (
              <div className="muted" style={{fontSize:11.5, padding:'24px 6px', textAlign:'center', lineHeight:1.5}}>
                Pick a channel in section 3 to see the relevant specs, sizes, and limits.
              </div>
            ) : (
              <>
                <div>
                  <div className="field-lbl" style={{marginBottom:8}}>{specReminders.sectionTitle}</div>
                  <div className="facet" style={{margin:0}}>
                    <div style={{display:'grid',gridTemplateColumns:'1fr auto',rowGap:6,columnGap:10,fontSize:11.5}}>
                      {specReminders.rows.map((r, i) => (
                        <Fragment key={i}>
                          <div className="muted">{r[0]}</div>
                          <div className="mono" style={{fontWeight:600}}>{r[1]}</div>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="field-lbl" style={{marginBottom:8}}>Limits</div>
                  <div className="facet" style={{margin:0}}>
                    <div style={{display:'grid',gridTemplateColumns:'1fr auto',rowGap:6,columnGap:10,fontSize:11.5}}>
                      {specReminders.limits.map((r, i) => (
                        <Fragment key={i}>
                          <div className="muted">{r[0]}</div>
                          <div className="mono" style={{fontWeight:600}}>{r[1]}</div>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="muted" style={{fontSize:10.5,lineHeight:1.5,letterSpacing:'.02em'}}>
                  <b style={{color:'var(--ink-1)'}}>Heads-up:</b> uploads that don't match the channel's spec are flagged inline. You can replace them without losing the rest of your work.
                </div>
              </>
            )}
          </div>
        </BuilderSummary>
      </div>

    </div>
  );
}

export { NewCreativeFlow  };
